import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { LazyLoadedWidgets } from 'src/app/app-routing-lazy';
import { IProductInterviewConfig, IProductSmartActionReference, ProductCategoryNames } from 'src/app/models/case';
import { ActionConfig, StageTrackerConfig } from 'src/app/models/product';
import { Role } from 'src/app/models/role';
import { ICustomComposition } from 'src/app/models/template-manager';
import { ITaskCommandWrapper } from 'src/app/services/task/task.state.models';
import { environment } from 'src/environments/environment';

import {
    IActionContext,
    ISmartActionContext,
    ISmartActionMetadata,
    SmartActionContext,
    SmartActionContextItem,
    SmartActionContextType,
} from '../../models/smart-actions.models';
import { AssessmentType, EvaluationServerityDescriptions, EvaluationSeverityMode } from '../interview/interview.model';
import { CommandParserService } from '../task/command/command-parser.service';
import { UserService } from '../user/user.service';
import { BaseProductService } from './-base-product.service';
import { BaseProductStateModel } from './base-product-state.models';
import {
    IAdvanceStageInput,
    IProductCaseRollbackCaseStageMetadata,
    IProductCaseRollbackCaseStagLazyComponentConfig,
    IRunInterviewConfig,
} from './product-smart-actions.models';

export function addSetCaseOwnerAction(productService: BaseProductService<BaseProductStateModel>, userRole: Role, actions: SmartActionContextItem[]) {

    if (userRole && userRole.inRoles(['ADVOCATEMANAGER'])) {
        const setCaseOwnerAction = SmartActionsStandard.setCaseOwner(productService);
        if (setCaseOwnerAction != null) {
            if (actions.length > 0) {
                actions.push(SmartActionsStandard.separator);
            }
            actions.push(setCaseOwnerAction);
        }
    }
}

export const qaReject = (productService: BaseProductService<BaseProductStateModel>, stage: string) => {


    const id = `member-${ProductCategoryNames[productService.productCategory]}-qareject`;
    const displayName = 'Reject';
    const description = 'Rollback case and process the interview again';
    const icon = 'fal fa-undo-alt';

    const rollBackAction = SmartActionsStandard.rollbackCaseStage(productService, id, displayName, description, icon, 'Reject', stage);

    return rollBackAction;
};

export function standardEnrollment(productService: BaseProductService<BaseProductStateModel>, allowedRoles: string[], userRole: Role, advanceStageInput: IAdvanceStageInput, actionContext: IActionContext, useInterview?: boolean): SmartActionContextItem[] {
    const actions: SmartActionContextItem[] = [];
    if (userRole.inRoles(allowedRoles)) {
        const currentUrl = window.location.href;
        const interview = (productService.interviews || []).find(ai => ai.type.toUpperCase() === AssessmentType.Enrollment.toUpperCase());

        useInterview = (useInterview === undefined ? true : useInterview);
        useInterview = useInterview && environment.allowEnrollment;

        // TODO: Remove true when enrollment become active
        if (currentUrl.match(`${productService.productCategory.toLowerCase()}/${AssessmentType.Enrollment.toLowerCase()}`)) {
            actions.push(SmartActionsStandard.showInterviewData(productService, AssessmentType.Enrollment));
        } else {

            if (useInterview !== null) {
                if (!useInterview || interview == null) {
                    actions.push(SmartActionsStandard.advanceCaseStage(productService, null, null, null, 'Confirm Action', 'Do you want to continue this case?'));
                } else {
                    actions.push(SmartActionsStandard.enrollment(productService));

                }
            }
        }

        // actions.push(SmartActionsStandard.separator);
        // actions.push(SmartActionsStandard.notinterested(productService));

        // // If the user can conduct the interview, don't offer the oportunity to select directly the not qualified action.
        // const noQualifiedDisabled = useInterview && !!interview;
        // actions.push(SmartActionsStandard.notqualified(productService, noQualifiedDisabled));
    }


    return actions;
}

export function standardEligibility(productService: BaseProductService<BaseProductStateModel>, allowedRoles: string[], userRole: Role, availableInterviews: IProductInterviewConfig[], advanceStageInput: IAdvanceStageInput, actionContext: IActionContext, useInterview?: boolean, allowEligibility?: boolean, interviewConfig?: IRunInterviewConfig): SmartActionContextItem[] {
    const actions: SmartActionContextItem[] = [];
    if (userRole.inRoles(allowedRoles)) {
        const currentUrl = window.location.href;
        const interview = (availableInterviews || []).find(ai => ai.type === AssessmentType.Eligibility.toUpperCase());

        useInterview = (useInterview == null ? true : useInterview);
        useInterview = useInterview && environment.allowEligibility;

        if (allowEligibility) {
            if (currentUrl.match(`${productService.productCategory.toLowerCase()}/${AssessmentType.Eligibility.toLowerCase()}`)) {
                actions.push(SmartActionsStandard.showInterviewData(productService, AssessmentType.Eligibility));
            } else {

                const interviewConfig = productService.getInterview('Eligibility');
                // TODO: Remove true when enrollment become active
                if (useInterview === false || !interviewConfig) {
                    actions.push(SmartActionsStandard.advanceCaseStage(productService, null, null, null, 'Confirm Action', 'Do you want to continue this case?'));
                    actions.push(SmartActionsStandard.separator);
                }
            }
        }

        actions.push(SmartActionsStandard.notinterested(productService));

        if (useInterview === false || interview == null) {
            actions.push(SmartActionsStandard.notqualified(productService));
        }
    } else {
        console.log(`SmartActions: No smart action for ${userRole.name}`);
    }

    return actions;
}


export const SmartActionsStandard = {
    eligibility: (productService: BaseProductService<BaseProductStateModel>, labelName: string = null) => new SmartActionContextItem({
        id: `${ProductCategoryNames[productService.productCategory]}-eligibility`,
        displayName: labelName || 'Eligibility Interview',
        description: `The eligibility process for ${ProductCategoryNames[productService.productCategory].toUpperCase()}`,
        icon: 'fal fa-tasks',
        roles: [],
        target: productService,
        command: (context: ISmartActionMetadata) => {
            const sourceAction = context.sourceAction;
            const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
            return productServiceObj.router.navigate(['/', 'member', productServiceObj.memberService.base64Sync, ProductCategoryNames[productServiceObj.productCategory], 'eligibility']);
        }
    } as SmartActionContextItem),

    forcedEligibility: (productService: BaseProductService<BaseProductStateModel>) => new SmartActionContextItem({
        id: `${ProductCategoryNames[productService.productCategory]}-eligibility`,
        displayName: 'New Case',
        description: `Create a new case for ${ProductCategoryNames[productService.productCategory].toUpperCase()}`,
        icon: 'fal fa-plus',
        roles: [],
        target: productService,
        confirmation: {
            id: 'member-forceeligibility',
            title: 'Create a new <strong>Case</strong>',
            details: `Are you sure you want to create a <strong>new case</strong>?`,
            icon: 'fal fa-exclamation-triagle',
            okLabel: 'Yes',
            closeLabel: 'Cancel'
        },
        command: async (context: ISmartActionMetadata) => {
            const sourceAction = context.sourceAction;
            const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
            productServiceObj.router.navigate(['/', 'member', productServiceObj.memberService.base64Sync, ProductCategoryNames[productServiceObj.productCategory], 'eligibility']);
        }
    } as SmartActionContextItem),

    enrollment: (productService: BaseProductService<BaseProductStateModel>) => new SmartActionContextItem({
        id: `${ProductCategoryNames[productService.productCategory]}-enrollment`,
        displayName: 'Enrollment Interview',
        description: `The enrollment process for ${ProductCategoryNames[productService.productCategory].toUpperCase()}`,
        icon: 'fal fa-calculator',
        target: productService,
        command: (context: ISmartActionMetadata) => {
            const sourceAction = context.sourceAction;
            const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
            productServiceObj.router.navigate(['/', 'member', productServiceObj.memberService.base64Sync, ProductCategoryNames[productServiceObj.productCategory], 'enrollment']);
        }
    } as SmartActionContextItem),


    interview: (productService: BaseProductService<BaseProductStateModel>) => new SmartActionContextItem({
        id: `member-interview`,
        displayName: 'Member setting interview',
        description: 'Configure member using a set of predefined questions',
        icon: 'fal fa-tools',
        target: productService,
        command: (context: ISmartActionMetadata) => {
            const sourceAction = context.sourceAction;
            const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
            productServiceObj.router.navigate(['/', 'member', productServiceObj.memberService.base64Sync, 'interview']);
        }
    } as SmartActionContextItem),

    customView: (productService: BaseProductService<BaseProductStateModel>, customViewId: string, displayName: string, description?: string, icon?: string) => {
        const viewId = customViewId;
        return new SmartActionContextItem({
            id: `${ProductCategoryNames[productService.productCategory]}-customview-${customViewId}`,
            displayName,
            description,
            icon,
            actionMetadata: { viewId },
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(viewId);
            }
        } as SmartActionContextItem);
    },

    showInterviewData: (productService: BaseProductService<BaseProductStateModel>, assessmentType: AssessmentType) => {
        return new SmartActionContextItem({
            id: `${ProductCategoryNames[productService.productCategory]}-interviewdata`,
            displayName: `Show interview data`,
            description: `Show ${AssessmentType[assessmentType]} data`,
            icon: 'fal fa-eye',
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_INTERVIEWDATA, { target: productServiceObj, lazyComponentHandler });
            }
        } as SmartActionContextItem);
    },

    notes: (productService: BaseProductService<BaseProductStateModel>) => {
        return new SmartActionContextItem({
            id: 'member-notes',
            displayName: 'Notes',
            description: 'Notes management',
            icon: 'fal fa-file',
            target: productService,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                productServiceObj.router.navigate(['/', 'member', productServiceObj.memberService.base64Sync, 'notes']);

            },
            confirmation: {
                id: 'member-notes',
                title: 'Navigate to Notes',
                details: `Do you confirm that you want to navigate to <strong>notes</strong>`,
                icon: 'fal fa-exclamation-triagle',
                okLabel: 'Go...',
                closeLabel: 'Hide'
            }

        } as SmartActionContextItem);
    },

    notinterested: <T extends BaseProductStateModel>(productService: BaseProductService<T>, contextType?: SmartActionContextType) => {
        return new SmartActionContextItem({
            id: `member-${ProductCategoryNames[productService.productCategory]}-notinterested`,
            displayName: 'Complete Case',
            description: 'A list of possible reasons why a case might be completed',
            icon: 'fal fa-list-alt',
            actionMetadata: { contextType },
            target: productService,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_NOTINTERESTED, { target: productServiceObj, lazyComponentHandler, actionMetadata: sourceAction.actionMetadata });
            }

        } as SmartActionContextItem);
    },

    notqualified: <T extends BaseProductStateModel>(productService: BaseProductService<T>, disabled?: boolean) => {
        return new SmartActionContextItem({
            id: `member-${ProductCategoryNames[productService.productCategory]}-notqualified`,
            displayName: 'Not Qualified',
            description: 'Not Qualified',
            icon: 'fal fa-ban',
            disabled,
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_NOTQUALIFIED, { target: productServiceObj, lazyComponentHandler });
            }

        } as SmartActionContextItem);
    },

    advanceCaseStage: (productService: BaseProductService<BaseProductStateModel>, label: string, description: string, icon: string, confirmationTitle: string, confirmationDetails: string, action?: string, reason?: any, metadata?: any, disabled?: boolean) => {
        return new SmartActionContextItem({
            id: `${ProductCategoryNames[productService.productCategory]}-advancestage`,
            displayName: label || 'Next',
            description,
            icon: icon === null || icon.length === 0 ? 'fal fa-arrow-alt-circle-right' : icon,
            disabled,
            target: productService,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                await productServiceObj.advanceStage(action || 'Next', reason, metadata);
                return null;
            },
            confirmation: {
                title: confirmationTitle || 'Confirm Action',
                details: confirmationDetails || 'Do you want to continue this case?',
                okLabel: 'Ok',
                closeLabel: 'Cancel'
            }

        } as SmartActionContextItem);
    },

    advanceCaseStageV2: <T extends BaseProductStateModel>(productService: BaseProductService<T>, externalConfig: IProductSmartActionReference) => {
        return new SmartActionContextItem({
            id: `${ProductCategoryNames[productService.productCategory]}-advancestage`,
            displayName: externalConfig.label || 'Next',
            description: externalConfig.label,
            icon: externalConfig.icon ? externalConfig.icon : 'fal fa-arrow-alt-circle-right',
            disabled: externalConfig.disabled,
            target: productService,
            actionMetadata: externalConfig,
            command: async (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const externalConfig = sourceAction.actionMetadata as IProductSmartActionReference
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                await productServiceObj.advanceStage(externalConfig.context?.action || 'Next', externalConfig.context?.reason, externalConfig.context?.metadata);
                return null;
            },
            confirmation: {
                title: externalConfig.confirmation?.title || 'Confirm Action',
                details: externalConfig.confirmation?.details || 'Do you want to continue this case?',
                okLabel: 'Ok',
                closeLabel: 'Cancel',
                detailsExternal: externalConfig.confirmation?.details != null
            }

        } as SmartActionContextItem);
    },

    rollbackCaseStage: <T extends BaseProductStateModel>(productService: BaseProductService<T>, id?: string, displayName?: string, description?: string, icon?: string, reason?: string, rollbackStage?: string) => {
        if (productService.caseStageSync && productService.caseStageSync.rollbackTargets && productService.caseStageSync.rollbackTargets.length > 0) {

            const destinationStage = productService.stagesSync.find(s => s.name === rollbackStage);
            const hideSelection = destinationStage != null;

            return new SmartActionContextItem({
                id: id || `${ProductCategoryNames[productService.productCategory]}-rollback`,
                displayName: displayName || 'Rollback',
                description: description || 'Case Stage Rollback',
                icon: icon || 'fal fa-arrow-alt-circle-left',
                actionMetadata: { hideSelection, targetStage: destinationStage, reason } as IProductCaseRollbackCaseStageMetadata,
                target: productService,
                command: (context: ISmartActionMetadata) => {
                    const sourceAction = context.sourceAction;
                    const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                    const lazyComponentHandler = context.lazyComponentHandler;
                    lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_CASESTAGEROLLBACK, { target: productServiceObj, lazyComponentHandler, actionMetadata: sourceAction.actionMetadata } as IProductCaseRollbackCaseStagLazyComponentConfig);
                }
            } as SmartActionContextItem);
        }
        return null;
    },

    completeWithNewCaseV2: async <T extends BaseProductStateModel>(productService: BaseProductService<T>) => {

        const shouldCreateCase = await productService.shouldCreateCase(productService.currentCaseSync, AssessmentType.Eligibility);

        if (shouldCreateCase) {
            return new SmartActionContextItem({
                id: `${ProductCategoryNames[productService.productCategory]}-eligibility`,
                displayName: 'New Case',
                description: `Create a new case for ${ProductCategoryNames[productService.productCategory].toUpperCase()}`,
                icon: 'fal fa-plus',
                roles: [],
                confirmation: {
                    id: 'member-forceeligibility',
                    title: 'Create a new <strong>Case</strong>',
                    details: `Are you sure you want to create a <strong>new case</strong>?`,
                    icon: 'fal fa-exclamation-triagle',
                    okLabel: 'Yes',
                    closeLabel: 'Cancel'
                },
                command: async (context: ISmartActionMetadata) => {
                    const sourceAction = context.sourceAction;
                    const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                    productServiceObj.addCase().toPromise();
                }
            } as SmartActionContextItem);
        }
    },

    rollbackCaseStageV2: <T extends BaseProductStateModel>(productService: BaseProductService<T>, externalConfig: IProductSmartActionReference) => {
        const targetStage = productService.stagesSync?.find(s => s.name === externalConfig.context?.rollbackStage);
        const hideSelection = targetStage != null;

        return new SmartActionContextItem({
            id: externalConfig.id,
            displayName: externalConfig.label || 'Rollback',
            description: externalConfig.tooltip || 'Case Stage Rollback',
            icon: externalConfig.icon || 'fal fa-arrow-alt-circle-left',
            actionMetadata: { hideSelection, targetStage, ...externalConfig.context } as IProductCaseRollbackCaseStageMetadata,
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const actionMetadata = sourceAction.actionMetadata as IProductCaseRollbackCaseStageMetadata;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;

                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_CASESTAGEROLLBACK, { target: productServiceObj, lazyComponentHandler, actionMetadata } as IProductCaseRollbackCaseStagLazyComponentConfig);
            }
        } as SmartActionContextItem);
    },

    setCaseOwner: (productService: BaseProductService<BaseProductStateModel>, id?: string, displayName?: string, description?: string, icon?: string, hideSelection?: boolean) => {
        return ({
            id: id || `${ProductCategoryNames[productService.productCategory]}-setcaseowner`,
            displayName: displayName || 'Set Case Owner',
            description: description || 'Set Case Owner',
            icon: icon || 'fal fa-user',
            actionMetadata: { hideSelection, caseOwner: productService.caseOwnerSync },
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_SETCASEOWNER, { productService: productServiceObj, lazyComponentHandler, ...sourceAction.actionMetadata });
            }
        } as SmartActionContextItem);
    },

    setCaseOwnerV2: <T extends BaseProductStateModel>(productService: BaseProductService<T>, externalConfig: IProductSmartActionReference) => {
        return ({
            id: externalConfig.id || `${ProductCategoryNames[productService.productCategory]}-setcaseowner`,
            displayName: externalConfig.label || 'Set Case Owner',
            description: externalConfig.tooltip || 'Set Case Owner',
            icon: externalConfig.icon || 'fal fa-user',
            actionMetadata: { caseOwner: productService.caseOwnerSync, ...externalConfig.context },
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_SETCASEOWNER, { productService: productServiceObj, lazyComponentHandler, ...sourceAction.actionMetadata });
            }
        } as SmartActionContextItem);
    },

    noAction: (productService: BaseProductService<BaseProductStateModel>, config?: ActionConfig) => new SmartActionContextItem({
        id: `NOACTION`,
        disabled: true,
        target: productService,
        displayName: config && config.displayName || `No Action`,
        description: config && config.displayName || `No Action`,
        icon: 'fal fa-ban'

    } as SmartActionContextItem),

    toggleEvaluationSeverity: (productService: BaseProductService<BaseProductStateModel>, evaluationSeverity: EvaluationSeverityMode) => {
        const evaluationSeverityToggled = (evaluationSeverity === EvaluationSeverityMode.Lineant ? EvaluationSeverityMode.Strict : EvaluationSeverityMode.Lineant);

        return new SmartActionContextItem({
            id: `member-${ProductCategoryNames[productService.productCategory]}-toggleevaluationseverity`,
            displayName: `${EvaluationServerityDescriptions.getDescription(evaluationSeverity)}`,
            description: 'Toggle evaluation severity',
            icon: `fal fa-toggle-${(evaluationSeverity === EvaluationSeverityMode.Strict ? 'on' : 'off')} ${(evaluationSeverity === EvaluationSeverityMode.Strict ? 'text-active' : '')}`,
            target: productService,
            handler: productService,
            command: async (context: ISmartActionMetadata) => {
                const productServiceObj = (context.sourceAction.target as BaseProductService<BaseProductStateModel>);

                await productServiceObj.interviewHandler.toggleSeverityMode();
            }

        } as SmartActionContextItem);
    },

    lisSubmissionCode: <T extends BaseProductStateModel>(productService: BaseProductService<T>) => {

        return new SmartActionContextItem({
            id: `member-${ProductCategoryNames[productService.productCategory]}-submission-code`,
            displayName: 'Submission Code',
            description: 'Show the dialog that allows for entry of the LIS submission code',
            icon: 'fal fa-hashtag',
            target: productService,
            command: (context: ISmartActionMetadata) => {
                const sourceAction = context.sourceAction;
                const productServiceObj = (sourceAction.target as BaseProductService<BaseProductStateModel>);
                const lazyComponentHandler = context.lazyComponentHandler;
                lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.COMPONENT_LIS_SUBMISSION_CODE, { target: productServiceObj, lazyComponentHandler });
            }
        } as SmartActionContextItem);
    },

    VAResend5345: <T extends BaseProductStateModel>(productService: BaseProductService<T>) => {
        return new SmartActionContextItem({
            id: `member-${ProductCategoryNames[productService.productCategory]}-resend5345`,
            displayName: 'Resend 5345',
            description: '',
            icon: 'fal fa-hashtag',
            target: productService,
            command: async (context: ISmartActionMetadata) => {

                const customComp: ICustomComposition = {
                    showMailMenu: true,
                    showBackMenu: false,
                    packet: 'Resend5345',
                    target: "COMPOSE_MEMBER",
                    lazyComponentHandler: context.lazyComponentHandler
                };
                context.lazyComponentHandler.setCurrentCustomView(LazyLoadedWidgets.DOCUMENT_CUSTOM_COMPOSITE_SELECTION, customComp);
            }
        } as SmartActionContextItem);
    },

    external: <T extends BaseProductStateModel>(productService: BaseProductService<T>, externalConfig: IProductSmartActionReference) => {

        const smartActionId = externalConfig?.id?.toUpperCase();
        let result: SmartActionContextItem;
        switch (true) {
            case !!externalConfig.separator:
                return SmartActionsStandard.separator;
            case smartActionId === 'NOTINTERESTED':
                return SmartActionsStandard.notinterested(productService);
            case smartActionId === 'NOTQUALIFIED':
                return SmartActionsStandard.notqualified(productService);
            case smartActionId === 'ADVANCESTAGE':
                return SmartActionsStandard.advanceCaseStageV2(productService, externalConfig);
            case smartActionId === 'ROLLBACK':
                return SmartActionsStandard.rollbackCaseStageV2(productService, externalConfig);
            case smartActionId === 'SETCASEOWNER':
                return SmartActionsStandard.setCaseOwnerV2(productService, externalConfig);
            case smartActionId === 'LIS_SUBMISSIONCODE':
                return SmartActionsStandard.lisSubmissionCode(productService);
            case smartActionId === 'VA_RESEND5345':
                return SmartActionsStandard.VAResend5345(productService);
            case smartActionId === 'NEWCASE':
                return SmartActionsStandard.completeWithNewCaseV2(productService);

        }



        result = new SmartActionContextItem({
            displayName: externalConfig.label,
            description: externalConfig.label,
            icon: externalConfig.icon,
            order: externalConfig.order,
            target: productService,
            actionMetadata: externalConfig,
            command: async (context: ISmartActionMetadata) => {
                const productServiceObj = (context.sourceAction.target as BaseProductService<BaseProductStateModel>);
                const config = context.sourceAction.actionMetadata as IProductSmartActionReference;
                productServiceObj.taskService.host.runCommands([{
                    id: context.sourceAction.id,
                    command: config.command,
                    task: {
                        displayName: config.label,
                        description: config.tooltip,
                        icon: config.icon,
                        contextData: {
                            ...config.context,
                            type: config.context?.type || config.id,
                            productCategory: productService.productCategory
                        }
                    }
                } as ITaskCommandWrapper]);
            }
        } as SmartActionContextItem);

        return result;
    },

    separator: {
        separator: true
    } as SmartActionContextItem,
};

export const StageTrackerStandards = {

    complete: (productService: BaseProductService<BaseProductStateModel>) => {

        const reason = '';
        return new SmartActionContext({
            id: 'complete',
            displayName: reason.length === 0 ? 'Complete' : `Complete (${reason})`,
            description: 'Complete',
            actions: [
            ]
        } as ISmartActionContext);
    },


    noAction: (productService: BaseProductService<BaseProductStateModel>, config?: StageTrackerConfig) => new SmartActionContext({
        id: 'NOACTION',
        displayName: config && config.displayName || 'No Action',
        description: config && config.description || 'No Action',
        disabled: true,
        actions: [
        ]
    }),

    noMatchingStage: (productService: BaseProductService<BaseProductStateModel>) => new SmartActionContext({
        id: 'noMatchingStage',
        displayName: 'No Matching Stage',
        description: 'No matching stage. This lead to a data integration issue',
        disabled: true,
        actions: [
            SmartActionsStandard.noAction(productService)
        ]
    }),

};

export const StageTrackerStandardContexts = {
    noAction: <T extends BaseProductStateModel>(productService: BaseProductService<T>, config: StageTrackerConfig = {} as StageTrackerConfig) => {
        const result = StageTrackerStandards.noAction(null);
        return of(result);
    },

    noMatchingStage: (productService: BaseProductService<BaseProductStateModel>) => {
        const result = new SmartActionContext(StageTrackerStandards.noMatchingStage(productService));

        return result;
    },
};
